import { lazy } from 'react';
import * as Sentry from '@sentry/react';
import type { ComponentType, LazyExoticComponent } from 'react';

const CHUNK_RELOAD_COOLDOWN_MS = 60 * 1000;
const SERVICE_WORKER_ACTIVATION_TIMEOUT_MS = 3000;
const SERVICE_WORKER_UPDATE_TIMEOUT_MS = 3000;

const RETRY_STAGE_RELOAD = 'reload';
const RETRY_STAGE_GIVE_UP = 'give_up';
const RETRY_FEATURE_TAG = 'lazy-with-retry';
const CHUNK_RELOAD_TIMESTAMP_KEY = 'chunk_reload_attempted_at';
const RETRY_RELOAD_MESSAGE = 'Lazy chunk load failed, reloading page';
const RETRY_GIVE_UP_MESSAGE = 'Lazy chunk load failed without recovery';

const SKIP_WAITING_MESSAGE = { type: 'SKIP_WAITING' };

type LazyModule<P> = { default: ComponentType<P> };

type LazyImportFn<P> = () => Promise<LazyModule<P>>;

type RetryStage = typeof RETRY_STAGE_RELOAD | typeof RETRY_STAGE_GIVE_UP;

function hasRecentlyAttemptedReload(): boolean {
	let storedTimestamp: string | null;

	try {
		storedTimestamp = sessionStorage.getItem(CHUNK_RELOAD_TIMESTAMP_KEY);
	} catch {
		return true;
	}

	if (storedTimestamp === null) {
		return false;
	}

	const elapsedMs = Date.now() - Number(storedTimestamp);
	const isWithinCooldown = elapsedMs < CHUNK_RELOAD_COOLDOWN_MS;

	return isWithinCooldown;
}

function persistReloadTimestamp(): boolean {
	try {
		sessionStorage.setItem(CHUNK_RELOAD_TIMESTAMP_KEY, String(Date.now()));
		return true;
	} catch {
		return false;
	}
}

function reportChunkRetry(stage: RetryStage, message: string): void {
	Sentry.captureMessage(message, { level: 'warning', tags: { feature: RETRY_FEATURE_TAG, stage } });
}

async function waitForServiceWorkerActivation(waitingWorker: ServiceWorker): Promise<void> {
	await new Promise<void>((resolve) => {
		const timeoutId = window.setTimeout(resolve, SERVICE_WORKER_ACTIVATION_TIMEOUT_MS);

		const handleControllerChange = (): void => {
			window.clearTimeout(timeoutId);

			resolve();
		};

		navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange, { once: true });
		waitingWorker.postMessage(SKIP_WAITING_MESSAGE);
	});
}

async function updateServiceWorker(registration: ServiceWorkerRegistration): Promise<void> {
	let timeoutId = 0;

	const updateTimeout = new Promise<void>((resolve) => {
		timeoutId = window.setTimeout(resolve, SERVICE_WORKER_UPDATE_TIMEOUT_MS);
	});
	const updateRequest = registration.update();

	try {
		await Promise.race([updateRequest, updateTimeout]);
	} finally {
		window.clearTimeout(timeoutId);
	}
}

async function activateUpdatedServiceWorker(): Promise<void> {
	if (!('serviceWorker' in navigator)) {
		return;
	}

	try {
		const registration = await navigator.serviceWorker.getRegistration();

		if (!registration) {
			return;
		}

		await updateServiceWorker(registration);
		const waitingWorker = registration.waiting;

		if (!waitingWorker) {
			return;
		}

		await waitForServiceWorkerActivation(waitingWorker);
	} catch {
		return;
	}
}

async function recoverFromChunkLoadFailure(): Promise<boolean> {
	if (navigator.onLine === false) {
		return false;
	}

	if (hasRecentlyAttemptedReload()) {
		return false;
	}

	const isReloadPersisted = persistReloadTimestamp();

	if (!isReloadPersisted) {
		return false;
	}

	reportChunkRetry(RETRY_STAGE_RELOAD, RETRY_RELOAD_MESSAGE);
	await activateUpdatedServiceWorker();

	window.location.reload();

	return true;
}

async function importWithReload<P>(importFn: LazyImportFn<P>): Promise<LazyModule<P>> {
	try {
		const loadedModule = await importFn();

		return loadedModule;
	} catch (error) {
		const isReloading = await recoverFromChunkLoadFailure();

		if (!isReloading) {
			reportChunkRetry(RETRY_STAGE_GIVE_UP, RETRY_GIVE_UP_MESSAGE);

			throw error;
		}

		const pendingUntilReload = new Promise<never>(() => undefined);

		return pendingUntilReload;
	}
}

export function lazyWithRetry<P>(importFn: LazyImportFn<P>): LazyExoticComponent<ComponentType<P>> {
	const lazyComponent = lazy<ComponentType<P>>(() => importWithReload(importFn));
	return lazyComponent;
}
